/**
 * Seeds ChatGPT-subscription OAuth tokens into opencode's auth store.
 *
 * The API delivers CONVEYOR_OPENCODE_OAUTH = base64({access, refresh, expires})
 * (see apps/api project-key/methods/key-resolution.ts). We write it as the
 * `openai` entry of ~/.local/share/opencode/auth.json — the store the
 * opencode-openai-codex-auth plugin reads and lazily refreshes. That directory
 * is fuse-symlinked on GCP projects, so a refreshed store outlives the pod;
 * the seed therefore only wins when the existing entry is missing, malformed,
 * or STALER than the seed (re-sign-in on the site produces a newer seed —
 * that's the invalidation-recovery path). Best-effort like
 * ensureClaudeCredentials: IO failures log-and-continue, never throw.
 */
import { promises as fs } from "node:fs";
import { dirname, join } from "node:path";
import { homedir } from "node:os";
import { createServiceLogger } from "../../../utils/logger.js";

const logger = createServiceLogger("opencode-auth");

/** Bump deliberately: also pre-warmed in the pod image (Dockerfile.base). */
export const OPENCODE_CODEX_PLUGIN = "opencode-openai-codex-auth@4.4.0";
const PLUGIN_PACKAGE = "opencode-openai-codex-auth";

export interface OpenCodeOauthSeed {
  access: string;
  refresh: string;
  expires: number;
}

export function opencodeAuthPath(env: NodeJS.ProcessEnv): string {
  const dataHome = env.XDG_DATA_HOME ?? join(env.HOME ?? homedir(), ".local", "share");
  return join(dataHome, "opencode", "auth.json");
}

export function opencodeConfigPath(env: NodeJS.ProcessEnv): string {
  const configHome = env.XDG_CONFIG_HOME ?? join(env.HOME ?? homedir(), ".config");
  return join(configHome, "opencode", "opencode.json");
}

export function parseOauthSeed(b64: string | undefined): OpenCodeOauthSeed | null {
  if (!b64) return null;
  try {
    const parsed = JSON.parse(Buffer.from(b64, "base64").toString("utf8")) as Record<
      string,
      unknown
    >;
    if (
      typeof parsed.access === "string" &&
      typeof parsed.refresh === "string" &&
      typeof parsed.expires === "number"
    ) {
      return { access: parsed.access, refresh: parsed.refresh, expires: parsed.expires };
    }
    return null;
  } catch {
    return null;
  }
}

/** Seed only when the store entry is missing/malformed or older than the seed. */
export function shouldSeed(existingEntry: unknown, seed: OpenCodeOauthSeed): boolean {
  if (!existingEntry || typeof existingEntry !== "object") return true;
  const entry = existingEntry as Record<string, unknown>;
  if (entry.type !== "oauth") return true;
  if (typeof entry.access !== "string" || typeof entry.refresh !== "string") return true;
  if (typeof entry.expires !== "number") return true;
  return entry.expires < seed.expires;
}

async function readJsonFile(path: string): Promise<Record<string, unknown>> {
  try {
    return JSON.parse(await fs.readFile(path, "utf8")) as Record<string, unknown>;
  } catch {
    return {};
  }
}

async function writeJsonFile(path: string, value: Record<string, unknown>): Promise<void> {
  await fs.mkdir(dirname(path), { recursive: true });
  await fs.writeFile(path, `${JSON.stringify(value, null, 2)}\n`, { mode: 0o600 });
}

async function ensureAuthEntry(env: NodeJS.ProcessEnv, seed: OpenCodeOauthSeed): Promise<void> {
  const path = opencodeAuthPath(env);
  const store = await readJsonFile(path);
  if (!shouldSeed(store.openai, seed)) {
    logger.info("opencode oauth store is fresher than the seed; leaving it alone");
    return;
  }
  store.openai = {
    type: "oauth",
    access: seed.access,
    refresh: seed.refresh,
    expires: seed.expires,
  };
  await writeJsonFile(path, store);
  logger.info("seeded opencode oauth store entry");
}

async function ensurePluginConfig(env: NodeJS.ProcessEnv): Promise<void> {
  const path = opencodeConfigPath(env);
  const config = await readJsonFile(path);
  const plugins = Array.isArray(config.plugin) ? (config.plugin as unknown[]) : [];
  const isOurs = (p: unknown): boolean =>
    typeof p === "string" && (p === PLUGIN_PACKAGE || p.startsWith(`${PLUGIN_PACKAGE}@`));
  const hasExactPin = plugins.includes(OPENCODE_CODEX_PLUGIN);
  const hasStalePin = plugins.some((p) => isOurs(p) && p !== OPENCODE_CODEX_PLUGIN);
  // Already correct — don't churn the fuse file.
  if (hasExactPin && !hasStalePin) return;
  const kept = plugins.filter((p) => !isOurs(p));
  config.plugin = [...kept, OPENCODE_CODEX_PLUGIN];
  await writeJsonFile(path, config);
  logger.info("ensured opencode codex-auth plugin in config");
}

/**
 * Best-effort seed of opencode's oauth store + plugin config. No-op when
 * CONVEYOR_OPENCODE_OAUTH is absent or unparseable. Never throws.
 */
export async function seedOpenCodeOauth(env: NodeJS.ProcessEnv): Promise<void> {
  const seed = parseOauthSeed(env.CONVEYOR_OPENCODE_OAUTH);
  if (!seed) return;
  try {
    await ensureAuthEntry(env, seed);
    await ensurePluginConfig(env);
  } catch (err) {
    logger.warn(
      `failed to seed opencode oauth store: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}
